import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { SERVER_URL } from './config';
import { useAuth } from './AuthContext';
import Navigation from './components/Navigation';

const TABS = ['Flags', 'Posts', 'Users', 'Payments', 'Earnings'];

function AdminDashboard() {
  const { user, token, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'Users';
  const setTab = t => setSearchParams({ tab: t }, { replace: true });
  const [data, setData] = useState({ flags: [], posts: [], users: [], payments: [], earnings: null });
  const [search, setSearch] = useState('');
  const [fetching, setFetching] = useState(false);
  const [postsPage, setPostsPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [postsTotal, setPostsTotal] = useState(0);
  const [usersTotal, setUsersTotal] = useState(0);
  const PAGE_SIZE = 50;
  const [removeModal, setRemoveModal] = useState(null); // { post }
  const [removeReason, setRemoveReason] = useState('');
  const [refundModal, setRefundModal] = useState(null); // { gift }
  const [paymentSort, setPaymentSort] = useState({ key: 'created_at', dir: 'asc' });
  const [paymentFilter, setPaymentFilter] = useState('all'); // 'all' | 'pool' | 'direct'
  const [collapsedPools, setCollapsedPools] = useState(new Set());
  const [paymentError, setPaymentError] = useState(null);
  const [disbursedIds, setDisbursedIds] = useState(new Set());
  const [hideDisbursed, setHideDisbursed] = useState(true);
  const [expandedPosts, setExpandedPosts] = useState(new Set());

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate('/');
  }, [user, loading, navigate]);

  const authHeaders = { Authorization: `Bearer ${token}` };
  const fmtDate = s => s ? new Date(s.endsWith('Z') ? s : s + 'Z').toLocaleString() : '—';

  const fetchFlags = useCallback(async () => {
    const res = await fetch(`${SERVER_URL}/admin/flags`, { headers: authHeaders });
    if (res.ok) { const json = await res.json(); setData(d => ({ ...d, flags: json })); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPosts = useCallback(async (q = '', page = 1) => {
    const res = await fetch(`${SERVER_URL}/admin/posts?q=${encodeURIComponent(q)}&page=${page}`, { headers: authHeaders });
    if (res.ok) { const json = await res.json(); setData(d => ({ ...d, posts: json.posts })); setPostsTotal(json.total); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = useCallback(async (q = '', page = 1) => {
    const res = await fetch(`${SERVER_URL}/admin/users?q=${encodeURIComponent(q)}&page=${page}`, { headers: authHeaders });
    if (res.ok) { const json = await res.json(); setData(d => ({ ...d, users: json.users })); setUsersTotal(json.total); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPayments = useCallback(async () => {
    const res = await fetch(`${SERVER_URL}/paypal/pending-disbursements`, { headers: authHeaders });
    if (res.ok) { const json = await res.json(); setData(d => ({ ...d, payments: json.gifts })); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEarnings = useCallback(async () => {
    const res = await fetch(`${SERVER_URL}/admin/earnings`, { headers: authHeaders });
    if (res.ok) { const json = await res.json(); setData(d => ({ ...d, earnings: json })); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user?.is_admin) return;
    setPaymentError(null);
    if (tab === 'Flags') fetchFlags();
    if (tab === 'Posts') fetchPosts(search, postsPage);
    if (tab === 'Users') fetchUsers(search, usersPage);
    if (tab === 'Payments') fetchPayments();
    if (tab === 'Earnings') fetchEarnings();
  }, [tab, user, postsPage, usersPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const action = async (url, method = 'PUT') => {
    setFetching(true);
    try {
      await fetch(`${SERVER_URL}${url}`, { method, headers: authHeaders });
      if (tab === 'Flags') await fetchFlags();
      if (tab === 'Posts') await fetchPosts(search, postsPage);
      if (tab === 'Users') await fetchUsers(search, usersPage);
    } finally {
      setFetching(false);
    }
  };

  const disburseGift = async (giftId) => {
    setPaymentError(null);
    setFetching(true);
    try {
      const res = await fetch(`${SERVER_URL}/paypal/disburse/${giftId}`, { method: 'POST', headers: authHeaders });
      if (res.ok) {
        setDisbursedIds(prev => new Set([...prev, giftId]));
      } else {
        const err = await res.json().catch(() => ({}));
        setPaymentError(err.detail || 'Disbursement failed.');
      }
    } catch {
      setPaymentError('Disbursement failed — network error.');
    } finally {
      setFetching(false);
    }
  };

  const disbursePool = async (postId) => {
    setPaymentError(null);
    setFetching(true);
    try {
      const res = await fetch(`${SERVER_URL}/paypal/pool-disburse/${postId}`, { method: 'POST', headers: authHeaders });
      if (res.ok) {
        const json = await res.json();
        if (json.succeeded?.length) {
          setDisbursedIds(prev => new Set([...prev, ...json.succeeded]));
        }
        if (json.failed?.length) {
          const details = json.failed.map(f => f.error || 'unknown error').join('; ');
          setPaymentError(`${json.failed.length} contribution${json.failed.length !== 1 ? 's' : ''} failed to disburse: ${details}`);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        setPaymentError(err.detail || 'Pool disbursement failed.');
      }
    } catch {
      setPaymentError('Pool disbursement failed — network error.');
    } finally {
      setFetching(false);
    }
  };

  const refundGift = async (giftId) => {
    setFetching(true);
    try {
      await fetch(`${SERVER_URL}/paypal/refund/${giftId}`, { method: 'POST', headers: authHeaders });
      await fetchPayments();
    } finally {
      setFetching(false);
      setRefundModal(null);
    }
  };

  const removePost = async () => {
    if (!removeModal) return;
    setFetching(true);
    try {
      await fetch(`${SERVER_URL}/admin/posts/${removeModal.postId}`, {
        method: 'DELETE',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: removeReason }),
      });
      if (tab === 'Flags') await fetchFlags();
      if (tab === 'Posts') await fetchPosts(search);
    } finally {
      setFetching(false);
      setRemoveModal(null);
      setRemoveReason('');
    }
  };

  const handleSearch = e => {
    setSearch(e.target.value);
    if (tab === 'Posts') { setPostsPage(1); fetchPosts(e.target.value, 1); }
    if (tab === 'Users') { setUsersPage(1); fetchUsers(e.target.value, 1); }
  };

  if (loading || !user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Admin Dashboard</h1>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => { setTab(t); setSearch(''); setPostsPage(1); setUsersPage(1); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t}
              {t === 'Flags' && (() => {
                const n = new Set(data.flags.filter(f => f.status === 'pending').map(f => f.post_id)).size;
                return n > 0 ? <span className="ml-1.5 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{n}</span> : null;
              })()}
            </button>
          ))}
        </div>

        {/* Search (Posts + Users) */}
        {(tab === 'Posts' || tab === 'Users') && (
          <input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder={`Search ${tab.toLowerCase()}…`}
            className="mb-4 w-full max-w-sm border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        )}

        {/* Flags tab */}
        {tab === 'Flags' && (() => {
          // Group flags by post_id
          const groups = [];
          const seen = {};
          for (const f of data.flags) {
            if (!seen[f.post_id]) {
              seen[f.post_id] = { post_id: f.post_id, post_title: f.post_title, reports: [] };
              groups.push(seen[f.post_id]);
            }
            seen[f.post_id].reports.push(f);
          }
          return (
            <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
              {groups.length === 0 ? (
                <p className="p-6 text-sm text-gray-500">No flags yet.</p>
              ) : groups.map(g => {
                const pendingCount = g.reports.filter(r => r.status === 'pending').length;
                const isExpanded = expandedPosts.has(g.post_id);
                return (
                  <div key={g.post_id}>
                    {/* Post-level summary row */}
                    <div className="flex items-center gap-3 px-4 py-3">
                      <button
                        onClick={() => setExpandedPosts(prev => {
                          const next = new Set(prev);
                          next.has(g.post_id) ? next.delete(g.post_id) : next.add(g.post_id);
                          return next;
                        })}
                        className="text-gray-400 hover:text-gray-600 text-xs select-none"
                      >{isExpanded ? '▾' : '▸'}</button>
                      {g.reports[0]?.post_user_id ? (
                        <Link
                          to={`/profile/${g.reports[0].post_user_id}#post-${g.post_id}`}
                          target="_blank"
                          className="font-medium text-gray-900 hover:text-indigo-600 hover:underline flex-1 truncate"
                        >{g.post_title}</Link>
                      ) : (
                        <span className="font-medium text-gray-900 flex-1 truncate">{g.post_title}</span>
                      )}
                      {pendingCount > 0 ? (
                        <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                          {pendingCount} pending
                        </span>
                      ) : (
                        <span className="bg-gray-100 text-gray-500 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                          {g.reports.length} resolved
                        </span>
                      )}
                      {pendingCount > 0 && (
                        <div className="flex gap-3 ml-2">
                          <button
                            disabled={fetching}
                            onClick={async () => {
                              setFetching(true);
                              try {
                                await fetch(`${SERVER_URL}/admin/posts/${g.post_id}/flags/dismiss-all`, { method: 'PUT', headers: authHeaders });
                                await fetchFlags();
                              } finally { setFetching(false); }
                            }}
                            className="text-xs text-gray-500 hover:text-gray-700 underline whitespace-nowrap"
                          >Dismiss all</button>
                          <button
                            disabled={fetching}
                            onClick={() => { setRemoveModal({ postId: g.post_id, title: g.post_title }); setRemoveReason(''); }}
                            className="text-xs text-red-500 hover:text-red-700 underline whitespace-nowrap"
                          >Remove post</button>
                        </div>
                      )}
                    </div>
                    {/* Expanded individual reports */}
                    {isExpanded && (
                      <div className="bg-gray-50 border-t border-gray-100 divide-y divide-gray-100">
                        {g.reports.map(r => (
                          <div key={r.id} className={`flex items-start gap-4 px-8 py-2.5 text-sm ${r.status !== 'pending' ? 'opacity-50' : ''}`}>
                            <span className="text-gray-700 font-medium whitespace-nowrap w-32 truncate">{r.reporter_name}</span>
                            <span className="text-gray-600 whitespace-nowrap">{r.reason}</span>
                            <span className="text-gray-400 flex-1 truncate">{r.details || '—'}</span>
                            <span className="text-gray-400 whitespace-nowrap">{fmtDate(r.created_at)}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                              r.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'
                            }`}>{r.status}</span>
                            {r.status === 'pending' && (
                              <button
                                disabled={fetching}
                                onClick={async () => {
                                  setFetching(true);
                                  try {
                                    await fetch(`${SERVER_URL}/admin/flags/${r.id}`, { method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'dismissed' }) });
                                    await fetchFlags();
                                  } finally { setFetching(false); }
                                }}
                                className="text-xs text-gray-400 hover:text-gray-600 underline whitespace-nowrap"
                              >Dismiss</button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Posts tab */}
        {tab === 'Posts' && (
          <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            {data.posts.length === 0 ? (
              <p className="p-6 text-sm text-gray-500">No posts found.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Title', 'Creator', 'Status', 'Raised', 'Flags', 'Created', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.posts.map(p => (
                    <tr key={p.id}>
                      <td className="px-4 py-3 font-medium text-gray-900 max-w-xs truncate">{p.title}</td>
                      <td className="px-4 py-3 text-gray-500">{p.creator_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          p.status === 'active' ? 'bg-green-100 text-green-800' :
                          p.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                          p.status === 'removed' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{p.status}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">${(p.current_amount || 0).toFixed(2)}{p.goal_amount ? ` / $${p.goal_amount.toFixed(2)}` : ''}</td>
                      <td className="px-4 py-3">
                        {p.flag_count > 0 && (
                          <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-medium">{p.flag_count}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                      <td className="px-4 py-3">
                        {p.status === 'removed' ? (
                          <button
                            disabled={fetching}
                            onClick={() => action(`/admin/posts/${p.id}/restore`)}
                            className="text-xs text-green-600 hover:text-green-800 underline"
                          >Restore</button>
                        ) : (
                          <button
                            disabled={fetching}
                            onClick={() => { setRemoveModal({ postId: p.id, title: p.title }); setRemoveReason(''); }}
                            className="text-xs text-red-500 hover:text-red-700 underline"
                          >Remove</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {postsTotal > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
              <span>{postsTotal} total · page {postsPage} of {Math.ceil(postsTotal / PAGE_SIZE)}</span>
              <div className="flex gap-2">
                <button disabled={postsPage === 1} onClick={() => setPostsPage(p => p - 1)} className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
                <button disabled={postsPage >= Math.ceil(postsTotal / PAGE_SIZE)} onClick={() => setPostsPage(p => p + 1)} className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
              </div>
            </div>
          )}
          </>
        )}

        {/* Users tab */}
        {tab === 'Users' && (
          <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            {data.users.length === 0 ? (
              <p className="p-6 text-sm text-gray-500">No users found.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Name', 'Email', 'Verified', 'PP Merchant', 'PP Receivable', 'PP Email', 'PP Merchant ID', 'Given', 'Received', 'Joined', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.users.map(u => (
                    <tr key={u.id} className={u.is_suspended ? 'bg-red-50' : ''}>
                      <td className="px-4 py-3 font-medium text-gray-900">{u.username}</td>
                      <td className="px-4 py-3 text-gray-500">{u.email}</td>
                      <td className="px-4 py-3">{u.is_verified ? '✅' : '—'}</td>
                      <td className="px-4 py-3">{u.paypal_has_merchant_id ? '✅' : '—'}</td>
                      <td className="px-4 py-3">{u.paypal_payments_receivable ? '✅' : u.paypal_has_merchant_id ? '⚠️' : '—'}</td>
                      <td className="px-4 py-3">{u.paypal_email_confirmed ? '✅' : u.paypal_has_merchant_id ? '⚠️' : '—'}</td>
                      <td className="px-4 py-3 text-gray-400 font-mono text-xs">{u.paypal_merchant_id || '—'}</td>
                      <td className="px-4 py-3 text-gray-700">${(u.total_given || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-gray-700">${(u.total_received || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3">
                        {u.is_suspended && <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-medium">Suspended</span>}
                      </td>
                      <td className="px-4 py-3">
                        {u.is_suspended ? (
                          <button
                            disabled={fetching}
                            onClick={() => action(`/admin/users/${u.id}/unsuspend`)}
                            className="text-xs text-green-600 hover:text-green-800 underline"
                          >Unsuspend</button>
                        ) : (
                          <button
                            disabled={fetching}
                            onClick={() => { if (window.confirm(`Suspend ${u.username}?`)) action(`/admin/users/${u.id}/suspend`); }}
                            className="text-xs text-red-500 hover:text-red-700 underline"
                          >Suspend</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {usersTotal > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
              <span>{usersTotal} total · page {usersPage} of {Math.ceil(usersTotal / PAGE_SIZE)}</span>
              <div className="flex gap-2">
                <button disabled={usersPage === 1} onClick={() => setUsersPage(p => p - 1)} className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
                <button disabled={usersPage >= Math.ceil(usersTotal / PAGE_SIZE)} onClick={() => setUsersPage(p => p + 1)} className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
              </div>
            </div>
          )}
          </>
        )}
        {/* Earnings tab */}
        {tab === 'Earnings' && (() => {
          const e = data.earnings;
          if (!e) return <p className="text-sm text-gray-500">Loading...</p>;
          return (
            <>
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[
                  { label: 'Total Gifts', value: e.total_gifts },
                  { label: 'Gift Volume', value: `$${e.total_volume.toFixed(2)}` },
                  { label: 'Platform Tips', value: `$${e.total_fees.toFixed(2)}` },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="text-xs text-gray-500 mb-1">{label}</div>
                    <div className="text-2xl font-bold text-gray-900">{value}</div>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                {e.gifts.length === 0 ? (
                  <p className="p-6 text-sm text-gray-500">No captured gifts yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        {['Date', 'Giver', 'Recipient', 'Amount', 'Tip', 'Type', 'Disbursed'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {e.gifts.map(g => (
                        <tr key={g.id}>
                          <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{fmtDate(g.created_at)}</td>
                          <td className="px-4 py-3 text-gray-900 whitespace-nowrap">{g.giver_name}</td>
                          <td className="px-4 py-3 text-gray-900 whitespace-nowrap">{g.recipient_name}</td>
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap">${g.amount.toFixed(2)}</td>
                          <td className="px-4 py-3 text-emerald-600 font-medium whitespace-nowrap">${g.platform_tip.toFixed(2)}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${g.post_id ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                              {g.post_id ? 'Pool' : 'Direct'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${g.disbursed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {g.disbursed ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          );
        })()}

        {/* Payments tab */}
        {tab === 'Payments' && (() => {
          const filtered = data.payments.filter(g => {
            if (paymentFilter === 'pool' && !g.post_id) return false;
            if (paymentFilter === 'direct' && g.post_id) return false;
            if (hideDisbursed && (g.disbursed || disbursedIds.has(g.id))) return false;
            return true;
          });
          const sortFn = (a, b) => {
            const { key, dir } = paymentSort;
            const av = a[key], bv = b[key];
            if (av == null) return 1;
            if (bv == null) return -1;
            const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
            return dir === 'asc' ? cmp : -cmp;
          };
          const poolMap = new Map();
          const directs = [];
          for (const g of filtered) {
            if (g.post_id) {
              if (!poolMap.has(g.post_id)) poolMap.set(g.post_id, []);
              poolMap.get(g.post_id).push(g);
            } else {
              directs.push(g);
            }
          }
          const togglePool = postId => setCollapsedPools(prev => {
            const next = new Set(prev);
            next.has(postId) ? next.delete(postId) : next.add(postId);
            return next;
          });

          const cols = [
            { label: 'Recipient', key: 'recipient_name' },
            { label: 'Giver', key: 'giver_name' },
            { label: 'Amount', key: 'amount' },
            { label: 'Type', key: 'post_id' },
            { label: 'Message', key: null },
            { label: 'Captured', key: 'captured_at' },
            { label: 'Hold', key: 'hold_remaining' },
            { label: 'Actions', key: null },
          ];

          const giftRow = g => {
            const disbursed = g.disbursed || disbursedIds.has(g.id);
            return (
              <tr key={g.id} className={disbursed ? 'bg-gray-50 opacity-60' : ''}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{g.recipient_name}</div>
                  <div className="text-gray-400 text-xs">{g.recipient_email}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{g.giver_name}</div>
                  <div className="text-gray-400 text-xs">{g.giver_email}</div>
                </td>
                <td className="px-4 py-3 text-gray-700 whitespace-nowrap">${g.amount.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${g.post_id ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {g.post_id ? 'Pool' : 'Direct'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500 max-w-[160px] truncate">{g.message || '—'}</td>
                <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{g.captured_at ? fmtDate(g.captured_at) : '—'}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {disbursed
                    ? <span className="bg-gray-100 text-gray-500 text-xs font-medium px-2 py-0.5 rounded-full">Disbursed</span>
                    : g.hold_remaining > 0
                      ? <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-0.5 rounded-full">{g.hold_remaining}d left</span>
                      : <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full">Ready</span>
                  }
                </td>
                <td className="px-4 py-3">
                  {!disbursed && (
                    <div className="flex gap-3">
                      <button disabled={fetching} onClick={() => disburseGift(g.id)} className="text-xs text-indigo-600 hover:text-indigo-800 underline whitespace-nowrap">Disburse</button>
                      <button disabled={fetching} onClick={() => setRefundModal(g)} className="text-xs text-red-500 hover:text-red-700 underline whitespace-nowrap">Refund</button>
                    </div>
                  )}
                </td>
              </tr>
            );
          };

          const sortedPools = [...poolMap.entries()].sort((a, b) => sortFn(a[1][0], b[1][0]));
          const sortedDirects = [...directs].sort(sortFn);

          return (
            <>
              {paymentError && (
                <div className="mb-3 px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex justify-between items-center">
                  <span>{paymentError}</span>
                  <button onClick={() => setPaymentError(null)} className="ml-4 text-red-400 hover:text-red-600 text-xs underline">Dismiss</button>
                </div>
              )}
              <div className="flex gap-2 mb-3 items-center flex-wrap">
                {[['all', 'All'], ['pool', 'Pools only'], ['direct', 'Direct only']].map(([f, label]) => (
                  <button key={f} onClick={() => setPaymentFilter(f)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${paymentFilter === f ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'}`}
                  >{label}</button>
                ))}
                <label className="ml-auto flex items-center gap-2 cursor-pointer select-none">
                  <span className="text-xs text-gray-500">Hide disbursed</span>
                  <div onClick={() => setHideDisbursed(v => !v)} className={`relative w-9 h-5 rounded-full transition-colors ${hideDisbursed ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${hideDisbursed ? 'translate-x-4' : 'translate-x-0'}`} />
                  </div>
                </label>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                {filtered.length === 0 ? (
                  <p className="p-6 text-sm text-gray-500">No pending disbursements.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        {cols.map(({ label, key }) => (
                          <th key={label}
                            onClick={() => key && setPaymentSort(s => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}
                            className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide ${key ? 'cursor-pointer select-none hover:text-gray-700' : ''}`}
                          >
                            {label}{key && paymentSort.key === key && (paymentSort.dir === 'asc' ? ' ↑' : ' ↓')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sortedPools.map(([postId, gifts]) => {
                        const collapsed = collapsedPools.has(postId);
                        const total = gifts.reduce((s, x) => s + x.amount, 0);
                        return [
                          <tr key={`pool-header-${postId}`} className="bg-purple-50">
                            <td colSpan={4} className="px-4 py-2 text-xs font-semibold text-purple-700">
                              <button onClick={() => togglePool(postId)} className="mr-2 text-purple-500 hover:text-purple-700 select-none text-base leading-none">
                                {collapsed ? '▶' : '▼'}
                              </button>
                              Pool: {gifts[0].post_title || postId} — {gifts.length} contribution{gifts.length !== 1 ? 's' : ''}, ${total.toFixed(2)} total
                            </td>
                            <td colSpan={4} className="px-4 py-2 text-right">
                              <button disabled={fetching} onClick={() => disbursePool(postId)} className="text-xs font-medium text-purple-700 hover:text-purple-900 underline whitespace-nowrap">Disburse all</button>
                            </td>
                          </tr>,
                          ...(!collapsed ? [...gifts].sort(sortFn).map(giftRow) : []),
                        ];
                      })}
                      {sortedDirects.length > 0 && sortedPools.length > 0 && (
                        <tr key="direct-header" className="bg-blue-50">
                          <td colSpan={8} className="px-4 py-2 text-xs font-semibold text-blue-700">
                            Direct gifts — {sortedDirects.length} gift{sortedDirects.length !== 1 ? 's' : ''}, ${sortedDirects.reduce((s, x) => s + x.amount, 0).toFixed(2)} total
                          </td>
                        </tr>
                      )}
                      {sortedDirects.map(giftRow)}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          );
        })()}
      </div>

      {/* Refund confirmation modal */}
      {refundModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Refund gift</h2>
            <p className="text-sm text-gray-500 mb-4">
              Refund <span className="font-medium text-gray-700">${refundModal.amount.toFixed(2)}</span> from{' '}
              <span className="font-medium text-gray-700">{refundModal.giver_name}</span> to{' '}
              <span className="font-medium text-gray-700">{refundModal.recipient_name}</span>?
              This will return funds to the giver and cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setRefundModal(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
              >Cancel</button>
              <button
                onClick={() => refundGift(refundModal.id)}
                disabled={fetching}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >{fetching ? 'Refunding…' : 'Refund'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Remove post modal */}
      {removeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Remove post</h2>
            {removeModal.title && (
              <p className="text-sm text-gray-500 mb-4">"{removeModal.title}"</p>
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-gray-400 font-normal">(sent to the user)</span>
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
              rows={3}
              placeholder="e.g. This post violated our community guidelines."
              value={removeReason}
              onChange={e => setRemoveReason(e.target.value)}
            />
            <p className="text-xs text-gray-400 mt-1 mb-4">
              The author will be notified by email and in-app and told to contact support if they think it was a mistake.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setRemoveModal(null); setRemoveReason(''); }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
              >Cancel</button>
              <button
                onClick={removePost}
                disabled={fetching}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >{fetching ? 'Removing…' : 'Remove post'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
